export class ErrorMessages {
  static apiError: string =
    "Request to API failed. Check if url is valid and try again.";
  static unexpected: string = "Unexpected error.";
  static badRequest: string = "Bad request.";
  static badCredentials: string = "User with given credentials not found.";
  static unauthorized: string = "Unauthorized. Please login.";
  static emailNotFound: string = "Email not found.";
  static jsonParseFailed: string =
    "Failed to parse/convert json to data model.";
}

export const catchErrorMessage = (e: unknown) => {
  if (e instanceof Error) {
    return e.message;
  } else if (typeof e === "string") {
    return e;
  }
  return "unknown error.";
};

/**
 * Extracts a user-friendly error message from API errors.
 * Handles the common error response shapes from the TFA API.
 */
export const extractApiErrorMessage = (
  error: unknown,
  fallbackMessage: string = "An error occurred"
): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (!error) {
    return fallbackMessage;
  }

  const err = error as any;
  const errorData = err.response?.data || err.data;

  if (!errorData) {
    return err.message || fallbackMessage;
  }

  if (typeof errorData === "string") {
    return errorData;
  }

  // Check common error fields
  if (errorData.detail) return String(errorData.detail);
  if (errorData.error) return String(errorData.error);
  if (errorData.message) return String(errorData.message);

  if (errorData.non_field_errors) {
    return Array.isArray(errorData.non_field_errors)
      ? errorData.non_field_errors.join(", ")
      : String(errorData.non_field_errors);
  }

  // DRF per-field 400s keep the message under the field's key, e.g.
  // {"field_type": ["Can't be changed once someone answers."]} (MAI-5083).
  const fieldErrors = Object.values(errorData).find(Array.isArray);
  if (fieldErrors?.length) return fieldErrors.join(", ");

  return fallbackMessage;
};

/**
 * Reads the machine-readable `error_code` a coded 400 body carries next to
 * its `error` text, so callers can branch on it instead of the message.
 */
export const extractApiErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object") return undefined;
  const err = error as any;
  const code = (err.response?.data ?? err.data)?.error_code;
  return typeof code === "string" ? code : undefined;
};

export class UnexpectedError extends Error {
  constructor(message?: string) {
    super(message ? message : ErrorMessages.unexpected);
    Object.setPrototypeOf(this, UnexpectedError.prototype);
  }
}

export class BadRequestError extends Error {
  constructor(message?: string) {
    super(message ? message : ErrorMessages.badRequest);
    Object.setPrototypeOf(this, BadRequestError.prototype);
  }
}

export class BadAuthCredentialsError extends Error {
  constructor(message?: string) {
    super(message ? message : ErrorMessages.badCredentials);
    Object.setPrototypeOf(this, BadAuthCredentialsError.prototype);
  }
}

export class UnauthorizedError extends Error {
  constructor(message?: string) {
    super(message ? message : ErrorMessages.unauthorized);
    Object.setPrototypeOf(this, BadAuthCredentialsError.prototype);
  }
}

export class JsonParsingError extends Error {
  constructor(message?: string) {
    super(message ? message : ErrorMessages.jsonParseFailed);
    Object.setPrototypeOf(this, JsonParsingError.prototype);
  }
}

export class NotFoundError extends Error {
  constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}
